import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Workflow, Rows3 } from "lucide-react";
import ProcessFlowCanvas from "@/components/ProcessFlowCanvas";
import ProcessSwimlanes from "@/components/ProcessSwimlanes";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SharedProcess() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [view, setView] = useState("flow");

  useEffect(() => {
    axios.get(`${API}/api/public/process/${token}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e.response?.data?.detail || "This link is invalid or has been revoked"));
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="text-center max-w-md" data-testid="shared-process-error">
          <Workflow className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
          <h1 className="font-display text-xl font-bold">Process not available</h1>
          <p className="text-sm text-muted-foreground mt-2">{err}</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const taskById = Object.fromEntries(Object.entries(data.task_titles || {}).map(([id, title]) => [id, { id, title }]));

  return (
    <div className="min-h-screen bg-background" data-testid="shared-process-page">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <p className="text-[11px] font-bold uppercase tracking-widest text-primary flex items-center gap-1.5"><Workflow className="h-3.5 w-3.5" /> Shared process diagram</p>
        <h1 className="font-display text-2xl sm:text-3xl font-bold mt-2" data-testid="shared-process-name">{data.name}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {data.project_name && <>Project: <b>{data.project_name}</b> · </>}Read-only view
        </p>
        {data.description && <p className="text-sm text-muted-foreground mt-3 max-w-2xl">{data.description}</p>}
        <div className="mt-5 flex items-center gap-1 rounded-lg border border-border p-1 w-fit" data-testid="shared-view-toggle">
          <button type="button" onClick={() => setView("flow")} className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors inline-flex items-center gap-1.5 ${view === "flow" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="shared-view-flow"><Workflow className="h-3.5 w-3.5" /> Flow</button>
          <button type="button" onClick={() => setView("lanes")} className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors inline-flex items-center gap-1.5 ${view === "lanes" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="shared-view-swimlanes"><Rows3 className="h-3.5 w-3.5" /> Swimlanes (by owner)</button>
        </div>
        <div className="mt-3 border border-border rounded-2xl bg-card/40 px-4">
          {view === "flow"
            ? <ProcessFlowCanvas steps={data.steps || []} taskById={taskById} />
            : <div className="py-4"><ProcessSwimlanes steps={data.steps || []} /></div>}
        </div>
        <p className="text-[11px] text-muted-foreground text-center mt-8">Shared via Project Management Orchestrator · PMO Maestro</p>
      </div>
    </div>
  );
}
